import { useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useDispatch } from "react-redux";
import AthleteSearchPanel from "./AthleteSearchPanel";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";
import { seedLikedOverride } from "../../../store/slices/athleteSlice";

function FavoriteAthleteSearch() {
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [isAthleteSelected, setIsAthleteSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const authUser = useAuthUser();
  const { user = {} } = authUser;

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 295,
    tabletOffset: 230,
    desktopOffset: 230,
  });

  const dispatch = useDispatch();
  const isCoachRole = user?.role === "coach" || user?.role === "university";

  const athleteSelected = (athlete) => {
    dispatch(seedLikedOverride({ athleteUserId: athlete.userId, liked: true }));
    setSelectedAthlete(athlete);
    setIsAthleteSelected(true);
  };

  const refreshFavorites = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="p-4 h-screen lg:h-full xl:h-full bg-gray-100">
      <div className="flex flex-col xl:flex-row lg:flex-row gap-4 h-full">
        <div className="w-full lg:w-2/4 xl:w-2/4">
          <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300 h-full">
            <h1 className="text-xl mb-3 uppercase">
              {isCoachRole ? "Favorite Athletes" : "Athletes"}
            </h1>
            {isCoachRole && (
              <div className="alert flex flex-row items-center bg-blue-100 p-2 rounded mt-0 mb-4">
                <div className="alert-icon flex items-center bg-blue-100 border-2 border-blue-300 justify-center h-5 w-5 flex-shrink-0 rounded-full">
                  <span className="text-blue-500">
                    <svg
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      className="h-4 w-4"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <div className="alert-content ml-4 w-full">
                  <div className="alert-description text-sm text-blue-700">
                    Click the thumbs up icon in a player’s profile to add them
                    to this view
                  </div>
                </div>
              </div>
            )}
            <AthleteSearchPanel
              authUser={authUser}
              selectedAthlete={selectedAthlete}
              onAthleteSelected={athleteSelected}
              adjustedHeight={adjustedHeight}
              favoritesOnly={isCoachRole}
              refreshKey={refreshKey}
            />
          </div>
        </div>

        {isAthleteSelected ? (
          <div className="w-full lg:w-2/4 xl:w-2/4">
            <div className="athletes-list h-full event_details_mob md:absolute xl:relative lg:relative md:col-span-7 xl:col-span-7 xl:space-y-0 xl:bg-transparent xl:border-gray-300 p-0 md:space-y-0 md:top-0 xl:top-0 lg:top-0">
              <div className="text-blue-600 pt-6 px-6 md:visible xl:hidden lg:hidden sm:visible">
                <NavLink onClick={() => setIsAthleteSelected(false)}>
                  <FontAwesomeIcon icon={faChevronLeft} /> Back
                </NavLink>
              </div>
              {selectedAthlete ? (
                <AthleteScore
                  userId={selectedAthlete.userId}
                  showOffers
                  showTrackittScore
                  showPositionScore
                  searchEvent={false}
                  showProfile
                  showRank
                  showLikes
                  onFavoritesRefresh={refreshFavorites}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="w-full p-2 mt-5 flex flex-col items-center">
                    <div className="hidden sm:hidden lg:block md:block">
                      <img
                        alt="Athlete Name"
                        src="/select_row.svg"
                        className="object-cover h-20 ml-18 pb-3"
                      />
                      <p className="text-center text-[#8E8E93] text-1xl">
                        Please select an Athlete to view their Profile
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default FavoriteAthleteSearch;
